"use client"

import * as React from "react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Textarea } from "./textarea"

type InputControlSize = "xs" | "sm" | "default" | "lg"
type InputGroupVariant = "default" | "inline"
type InputGroupAddonProps = {
  align?: "inline-start" | "inline-end" | "block-start" | "block-end"
}
type InputGroupButtonSize = "xs" | "sm" | "icon-xs" | "icon-sm"

const inputGroupRootSizes: Record<InputControlSize, string> = {
  xs: "h-5 rounded-sm",
  sm: "h-6",
  default: "h-7",
  lg: "h-8",
}

const inputGroupRootVariants: Record<InputGroupVariant, string> = {
  default:
    "border border-input bg-field focus-within:border-ring dark:bg-[color-mix(in_srgb,var(--colors-input)_30%,transparent)]",
  inline: "bg-transparent dark:bg-transparent",
}

const inputGroupAddonAlign: Record<
  NonNullable<InputGroupAddonProps["align"]>,
  string
> = {
  "inline-start": "pl-2",
  "inline-end": "pr-2",
  "block-start": "w-full justify-start px-2.5 pt-2",
  "block-end": "w-full justify-start px-2.5 pb-2",
}

const inputGroupButtonSizes: Record<InputGroupButtonSize, string> = {
  xs: "h-full gap-1 rounded-none px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
  sm: "",
  "icon-xs": "size-7 min-w-7 p-0",
  "icon-sm": "size-7 min-w-7 p-0",
}

const inputGroupStyles = {
  root:
    "relative flex w-full min-w-0 items-center overflow-hidden rounded-md",
  addon:
    "flex h-full select-none items-center justify-center gap-2 py-0 text-sm font-medium leading-sm text-muted-foreground [data-slot=input-group][data-disabled=true]_&:opacity-50 [&>kbd]:rounded-[calc(var(--radii-md)-5px)] [&>svg:not([class*='size-'])]:size-4 [data-slot=input-group][data-size=xs]_&:gap-1 [data-slot=input-group][data-size=xs]_&:px-1.5 [data-slot=input-group][data-size=xs]_&:text-caption [data-slot=input-group][data-size=xs]_&:leading-caption [data-slot=input-group][data-size=xs]_&:[&>svg:not([class*='size-'])]:size-3 [data-slot=input-group][data-size=sm]_&:gap-1 [data-slot=input-group][data-size=sm]_&:px-2 [data-slot=input-group][data-size=sm]_&:text-xs [data-slot=input-group][data-size=sm]_&:leading-xs [data-slot=input-group][data-size=sm]_&:[&>svg:not([class*='size-'])]:size-3.5 [data-slot=input-group][data-size=lg]_&:gap-2 [data-slot=input-group][data-size=lg]_&:px-2.5 [data-slot=input-group][data-size=lg]_&:text-sm [data-slot=input-group][data-size=lg]_&:leading-sm [data-slot=input-group][data-size=lg]_&:[&>svg:not([class*='size-'])]:size-4.5",
  button:
    "m-0 flex h-full shrink-0 items-center justify-center gap-1 rounded-none border-input border-l text-xs leading-xs shadow-none focus-visible:shadow-none focus-visible:outline-0",
  text:
    "flex items-center gap-2 text-sm leading-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [data-slot=input-group][data-size=xs]_&:gap-1 [data-slot=input-group][data-size=xs]_&:text-caption [data-slot=input-group][data-size=xs]_&:leading-caption [data-slot=input-group][data-size=xs]_&:[&_svg:not([class*='size-'])]:size-3 [data-slot=input-group][data-size=sm]_&:gap-1 [data-slot=input-group][data-size=sm]_&:text-xs [data-slot=input-group][data-size=sm]_&:leading-xs [data-slot=input-group][data-size=sm]_&:[&_svg:not([class*='size-'])]:size-3.5 [data-slot=input-group][data-size=lg]_&:gap-2 [data-slot=input-group][data-size=lg]_&:text-sm [data-slot=input-group][data-size=lg]_&:leading-sm [data-slot=input-group][data-size=lg]_&:[&_svg:not([class*='size-'])]:size-4.5",
  inputControl:
    "flex-1 rounded-none border-0 bg-transparent shadow-none outline-0 disabled:bg-transparent focus-visible:outline-0 dark:disabled:bg-transparent",
  textareaControl:
    "flex-1 resize-none rounded-none border-0 bg-transparent py-2 shadow-none outline-0 disabled:bg-transparent focus-visible:outline-0 dark:disabled:bg-transparent",
}

function InputGroup({
  className,
  size = "default",
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  size?: InputControlSize
  variant?: InputGroupVariant
}) {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      data-variant={variant}
      className={cn(
        inputGroupStyles.root,
        inputGroupRootSizes[size],
        inputGroupRootVariants[variant],
        className,
      )}
      {...props}
    />
  )
}

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & InputGroupAddonProps) {
  return (
    <div
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupStyles.addon, inputGroupAddonAlign[align], className)}
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus()
      }}
      {...props}
    />
  )
}

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size" | "type"> &
  {
    size?: InputGroupButtonSize
    type?: "button" | "submit" | "reset"
  }) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      size={size}
      className={cn(inputGroupStyles.button, inputGroupButtonSizes[size], className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        inputGroupStyles.text,
        className
      )}
      {...props}
    />
  )
}

function InputGroupInput({
  className,
  size = "default",
  ...props
}: Omit<React.ComponentProps<"input">, "size"> & {
  size?: InputControlSize
}) {
  return (
    <Input
      data-slot="input-group-control"
      size={size}
      className={cn(
        inputGroupStyles.inputControl,
        className
      )}
      {...props}
    />
  )
}

function InputGroupTextarea({
  className,
  size = "default",
  ...props
}: Omit<React.ComponentProps<"textarea">, "size"> & {
  size?: InputControlSize
}) {
  return (
    <Textarea
      data-slot="input-group-control"
      size={size}
      className={cn(
        inputGroupStyles.textareaControl,
        className
      )}
      {...props}
    />
  )
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
}
